import { Component, Input, OnChanges, SimpleChanges, EventEmitter, Output, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { LoaderService } from '../../loader/loader.service';

@Component({
	selector: 'app-tree-filter',
	templateUrl: './tree-filter.component.html',
	styleUrls: ['./tree-filter.component.css'],
})
export class TreeFilterComponent implements OnChanges, OnDestroy {
	//	@Input() FilterModel: any; // replace with actual type
	@Input() nodes: any[]; // replace with actual type
	@Input() excludedTypeIds: number[] = []; // Array of TypeIds to filter out (default: [9])
	@Input() selectedSchoolId: number | string | null = null; // ID of the school to select in the tree

	@Output() node = new EventEmitter<any>();

	filterModel = {
		show: true,
	};
	selectedNodeId: any;
	filteredNodes: any[] = [];
	isLoading: boolean = false;
	private loadingSubscription: Subscription;

	constructor(private loaderService: LoaderService) {
		this.loadingSubscription = this.loaderService.loadingGrowthChart$.subscribe((loading) => {
			// Defer so updates never run during the same CD tick as the tree template (avoids NG0100).
			Promise.resolve().then(() => {
				this.isLoading = loading;
			});
		});
	}

	ngOnDestroy(): void {
		if (this.loadingSubscription) {
			this.loadingSubscription.unsubscribe();
		}
	}

	onNodeChange(node): void {
		this.node.emit(node);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.nodes && changes.nodes.currentValue) {
			this.applyFilter();
		}
		if (changes.excludedTypeIds) {
			// Apply filter when excludedTypeIds changes (including initial value)
			if (this.nodes) {
				this.applyFilter();
			}
		}
		if (
			changes.selectedSchoolId &&
			changes.selectedSchoolId.currentValue !== null &&
			changes.selectedSchoolId.currentValue !== undefined
		) {
			// When selectedSchoolId changes, find and select the matching node
			this.selectNodeById(changes.selectedSchoolId.currentValue);
		}
	}

	private applyFilter(): void {
		if (this.nodes && Array.isArray(this.nodes)) {
			this.filteredNodes = this.filterNodes(this.nodes);
			if (this.filteredNodes.length > 0) {
				this.initializeNodes(this.filteredNodes);
				// If selectedSchoolId is provided, select that node; otherwise select first node
				if (this.selectedSchoolId !== null && this.selectedSchoolId !== undefined) {
					this.selectNodeById(this.selectedSchoolId);
				} else if (this.filteredNodes[0]) {
					this.selectedNodeId = this.filteredNodes[0].model.id;
					this.onNodeChange(this.filteredNodes[0].model);
					this.filteredNodes[0].expanded = true;
				}
			} else {
				this.filteredNodes = [];
			}
		}
	}

	onCheckboxChange(item) {
		this.onNodeChange(item.model);
		this.uncheckAllNodes(this.filteredNodes);
		item.checked = !item.checked;
		this.selectedNodeId = item.model.id;
		if (item.children) {
			item.children.forEach((sibling) => {
				this.checkAndDisableDescendants(sibling);
			});
		}
		//this.filterModel.show = true;
	}

	clearSelection(event: Event): void {
		event.stopPropagation(); // Prevent triggering the node click
		this.selectedNodeId = null;
		this.uncheckAllNodes(this.filteredNodes);
		this.onNodeChange(null); // Emit null to clear the selection
	}
	initializeNodes(nodes) {
		nodes.forEach((node) => {
			node.checked = true;
			if (node.children) {
				this.initializeNodes(node.children);
			}
		});
	}

	checkAndDisableDescendants(node) {
		node.checked = true;
		if (node.children) {
			node.children.forEach((child) => this.checkAndDisableDescendants(child));
		}
	}

	uncheckAllNodes(nodes) {
		nodes.forEach((node) => {
			node.checked = false;
			if (node.children) {
				this.uncheckAllNodes(node.children);
			}
		});
	}

	shouldShowNode(node: any): boolean {
		const typeId = node.model?.typeId;
		if (typeId === null || typeId === undefined) {
			return true;
		}
		// Handle both string and number types for typeId
		return !this.excludedTypeIds.some((excludedId) => Number(excludedId) === Number(typeId));
	}

	filterNodes(nodes: any[]): any[] {
		if (!nodes || !Array.isArray(nodes)) {
			return [];
		}

		return nodes
			.filter((node) => {
				const typeId = node.model?.typeId;
				if (typeId === null || typeId === undefined) {
					return true;
				}
				// Handle both string and number types for typeId
				return !this.excludedTypeIds.some((excludedId) => Number(excludedId) === Number(typeId));
			})
			.map((node) => {
				const filteredNode = { ...node };
				if (node.children && node.children.length > 0) {
					filteredNode.children = this.filterNodes(node.children);
				}
				return filteredNode;
			});
	}

	/**
	 * Recursively find and select a node by its ID
	 */
	private findNodeById(nodes: any[], targetId: number | string): any {
		if (!nodes || !Array.isArray(nodes)) {
			return null;
		}

		for (const node of nodes) {
			// Check if this node matches (compare as both string and number)
			if (node.model?.id == targetId || String(node.model?.id) === String(targetId)) {
				return node;
			}
			// Recursively search children
			if (node.children && node.children.length > 0) {
				const found = this.findNodeById(node.children, targetId);
				if (found) {
					return found;
				}
			}
		}
		return null;
	}

	/**
	 * Select a node by ID and expand parent nodes to make it visible
	 */
	private selectNodeById(nodeId: number | string): void {
		if (!this.filteredNodes || this.filteredNodes.length === 0) {
			return;
		}

		const foundNode = this.findNodeById(this.filteredNodes, nodeId);
		if (foundNode) {
			// Expand all parent nodes to make the selected node visible
			this.expandParents(this.filteredNodes, nodeId);

			// Select the node
			this.uncheckAllNodes(this.filteredNodes);
			this.setNodeChecked(foundNode, true);
			this.selectedNodeId = foundNode.model.id;
			this.onNodeChange(foundNode.model);
		}
	}

	/**
	 * Recursively expand parent nodes to make a target node visible
	 */
	private expandParents(nodes: any[], targetId: number | string): boolean {
		if (!nodes || !Array.isArray(nodes)) {
			return false;
		}

		for (const node of nodes) {
			if (node.model?.id == targetId || String(node.model?.id) === String(targetId)) {
				return true; // Found the target
			}
			if (node.children && node.children.length > 0) {
				if (this.expandParents(node.children, targetId)) {
					node.expanded = true; // Expand this parent
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * Set a node and its descendants as checked/unchecked
	 */
	private setNodeChecked(node: any, checked: boolean): void {
		if (!node) {
			return;
		}
		node.checked = checked;
		if (node.children && node.children.length > 0) {
			node.children.forEach((child: any) => this.setNodeChecked(child, checked));
		}
	}
}
